import { useEffect, type ReactNode } from "react";
import { useBlocker, useRouter } from "@tanstack/react-router";
import { Dialog } from "./ui";

function RouterBlocker() {
  // withResolver hands back proceed/reset so the decision can be a real dialog
  // rather than the browser's confirm box.
  const blocker = useBlocker({
    shouldBlockFn: () => true,
    // Browser unload is registered separately in UnsavedGuard so the guard also
    // works where there is no router, such as a component rendered on its own.
    enableBeforeUnload: false,
    withResolver: true,
  });
  return (
    <Dialog
      open={blocker.status === "blocked"}
      onClose={() => blocker.reset?.()}
      title="Unsaved changes"
      width={440}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={() => blocker.reset?.()}>
            Keep editing
          </button>
          <button type="button" className="btn danger" onClick={() => blocker.proceed?.()}>
            Discard and leave
          </button>
        </>
      }
    >
      <p style={{ margin: 0 }}>This page has changes you have not saved. Leaving now discards them.</p>
    </Dialog>
  );
}

/**
 * Protects a draft from being lost to navigation or to closing the tab. The
 * browser owns the unload prompt, so only in-app navigation carries our wording.
 */
export function UnsavedGuard({ dirty }: { dirty: boolean }) {
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const router = useRouter({ warn: false });
  return dirty && router ? <RouterBlocker /> : null;
}

/**
 * The action row for a form that is the whole page. It sticks to the bottom of
 * the viewport, so a change made at the end of a long settings page is saved
 * from where it was made rather than by scrolling back to find the button.
 *
 * It is always rendered, dirty or not: a bar that appeared on the first
 * keystroke would reflow the page under the cursor, and a Save that only exists
 * once something has changed is a Save nobody can find beforehand.
 */
export function SaveBar({
  dirty,
  saving = false,
  onSave,
  onDiscard,
  label,
  disabled = false,
  testId,
}: {
  dirty: boolean;
  saving?: boolean;
  onSave: () => void;
  onDiscard?: () => void;
  /** What this bar saves, named for pages that hold more than one card. */
  label?: string;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <div className={`save-bar${dirty ? " dirty" : ""}`} role={dirty ? "status" : undefined}>
      <UnsavedGuard dirty={dirty} />
      <button type="button" className="btn primary" onClick={onSave} disabled={!dirty || saving || disabled} data-testid={testId}>
        {saving ? "Saving…" : "Save changes"}
      </button>
      {dirty && onDiscard && (
        <button type="button" className="btn" onClick={onDiscard} disabled={saving}>
          Discard
        </button>
      )}
      {dirty && (
        <span className="save-bar-note">
          <strong>Unsaved changes</strong>
          {label ? <span className="muted small">to {label}</span> : null}
        </span>
      )}
    </div>
  );
}

/**
 * The same contract for a section that saves on its own: the notice and Discard
 * sit next to that section's Save, because a page-level bar could not say which
 * of several cards it meant.
 */
export function DirtyActions({ dirty, onDiscard, children }: { dirty: boolean; onDiscard?: () => void; children: ReactNode }) {
  return (
    <div className="dirty-actions">
      <UnsavedGuard dirty={dirty} />
      {dirty && (
        <span className="dirty-note">
          <strong>Unsaved changes</strong>
        </span>
      )}
      {dirty && onDiscard && (
        <button type="button" className="btn" onClick={onDiscard}>
          Discard
        </button>
      )}
      {children}
    </div>
  );
}

/** Stable enough for JSON-shaped API settings drafts. */
export function settingsEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
